import { useEffect, type ReactNode } from 'react';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { connectivity, isDown, useConnectivity } from '../api/connectivity';
import { OfflineError } from '../api/http';
import { signedOutHere } from '../api/storedReads';
import { Admin } from './Admin';
import { VoicePairs } from './VoicePairs';
import { AuthProvider, useAuth } from './AuthContext';
import { CampaignBoard } from './CampaignBoard';
import { Catalog } from './Catalog';
import { LoadoutEditor } from './LoadoutEditor';
import { TerrainDetail } from './TerrainDetail';
import { Changelog } from './Changelog';
import { Status } from './Status';
import { Designer } from './Designer';
import { DirectionPage } from './DirectionPage';
import { Library } from './Library';
import { MissionPage } from './MissionPage';
import { NewMissionDecide, NewMissionType } from './NewMissionSteps';
import { SessionPage } from './SessionPage';
import { SessionTechnical } from './SessionTechnical';
import { Home } from './Home';
import { Inspections } from './Inspections';
import { InspectionCampaignReview } from './InspectionCampaignReview';
import { InspectionCampaignStatus } from './InspectionCampaignStatus';
import { InspectionReview } from './InspectionReview';
import { InspectionSummary } from './InspectionSummary';
import { Landing } from './Landing';
import { MetaProvider, useMeta } from './MetaContext';
import { NewCampaign } from './NewCampaign';
import { NewMissionEnemy, NewMissionMap } from './NewMission';
import { NewMissionServer } from './NewMissionServer';
import { NewMissionForces } from './NewMissionForces';
import { NewMissionStart } from './NewMissionStart';
import { NewMissionIntel } from './NewMissionIntel';
import { useConnectionWatch } from './connectionHelpers';
import {
  PageLoading,
  PageProblem,
  ShellConnectionNotice,
  SignedOutOfflineNotice,
} from './PageStates';
import { Profile } from './Profile';
import { Proves } from './Proves';
import { Register } from './Register';
import { Reports } from './Reports';
import { Roadmap } from './Roadmap';
import { ComingUp } from './ComingUp';
import { ServerDetail } from './ServerDetail';
import { Servers } from './Servers';
import { Sessions } from './Sessions';
import { Settings } from './Settings';
import { SignIn } from './SignIn';
import { SignOut } from './SignOut';
import { nextQuery } from './returnPath';
import { Welcome } from './Welcome';

/** A signed-in account that has not answered the first sign-in's questions goes to /welcome first, once. */
function OnboardingGate({ children }: { children: ReactNode }) {
  const { me, loaded } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (
      loaded &&
      me.authenticated &&
      me.onboarded === false &&
      !location.pathname.startsWith('/welcome')
    ) {
      // Where they were going travels with them: Welcome sends them on to it when it is done.
      // From a door (the registration, say) it is the door's own `next` that goes on; from
      // anywhere else, the address they were at.
      const door = ['/signin', '/register', '/signout'].includes(location.pathname);
      const from = door
        ? new URLSearchParams(location.search).get('next')
        : `${location.pathname}${location.search}`;
      navigate(`/welcome${from === '/' ? '' : nextQuery(from)}`, { replace: true });
    }
  }, [loaded, me.authenticated, me.onboarded, location.pathname, location.search, navigate]);
  return children;
}

/**
 * The one place the app leaves for another host. It is an object with a method, not a bare
 * function, because jsdom refuses to let a test replace `window.location` and this redirect
 * has to be covered by one.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const leaving = {
  to(url: string) {
    window.location.replace(url);
  },
};

/** The host of an absolute address, or '' when it is empty or not an address at all. */
function hostOf(url: string): string {
  try {
    return url ? new URL(url).hostname : '';
  } catch {
    return '';
  }
}

/**
 * The paths a stranger may open on the platform host: the doors themselves,
 * and nothing else.
 *
 * The changelog was here until 2026-10-12, as the one page meant for somebody
 * with no account. The owner closed it with every other section ("neither
 * Novetats nor any other section must be accessible to unregistered users"),
 * and /api/changelog asks for a session too: the two doors close together, as
 * they open together (memory: a-public-page-needs-two-doors-opened). The link
 * the bot posts to Discord now lands on the sign-in page and comes back here
 * through `next`. A session's page closed too (the owner, 2026-10-14: "closed,
 * with an invitation"): `/s/<code>` is for somebody with an account, and the
 * invitation that brings a newcomer to it is `/s/<code>?invite=<code>`, which
 * sends them to registration and back (AuthGate).
 */
function openToStrangers(path: string): boolean {
  return path === '/signin' || path === '/register' || path === '/signout';
}

/**
 * The platform host shows a stranger nothing: whoever has no session is sent to the sign-in page,
 * carrying where they were going in `next`. Only the doors stay open. A stranger who arrives with
 * an invitation code (`?invite=<code>` on any address, a session's link included) goes to the
 * registration instead, with the code in and the address they were sent to as `next`: once
 * registered and signed in they land there. It shows the loading shapes until `/api/auth/me` has
 * answered, so a member's reload never flashes a page meant for nobody, nor a blank one.
 */
function AuthGate({ children }: { children: ReactNode }) {
  const { me, loaded, offline, refresh } = useAuth();
  const c = useConnectivity();
  const location = useLocation();
  const navigate = useNavigate();
  const open = openToStrangers(location.pathname);
  const stranger = loaded && !me.authenticated && !offline;
  useEffect(() => {
    if (!stranger || open) return;
    const params = new URLSearchParams(location.search);
    const invite = params.get('invite');
    if (invite) {
      params.delete('invite');
      const rest = params.toString();
      const to = `${location.pathname}${rest ? `?${rest}` : ''}`;
      navigate(`/register?invite=${encodeURIComponent(invite)}${nextQuery(to, '&')}`, {
        replace: true,
      });
      return;
    }
    const from = `${location.pathname}${location.search}`;
    navigate(`/signin${from === '/' ? '' : nextQuery(from)}`, { replace: true });
  }, [stranger, open, location.pathname, location.search, navigate]);
  if (!open && !loaded) {
    return (
      <main className="page">
        <PageLoading quiet />
      </main>
    );
  }
  // No platform and no stored copy of the account: say so, rather than send a
  // member to the sign-in page as if they had none (`AuthContext`'s `offline`).
  // When the account signed out here, that is why there is no copy: say that.
  if (!open && !me.authenticated && offline) {
    return (
      <main className="page narrow">
        {signedOutHere() ? (
          <SignedOutOfflineNotice
            kind={c.online ? 'unreachable' : 'offline'}
            signedOut
            onRetry={() => void refresh()}
          />
        ) : (
          <>
            <ShellConnectionNotice />
            <PageProblem error={new OfflineError()} onRetry={() => void refresh()} />
          </>
        )}
      </main>
    );
  }
  if (!open && !me.authenticated) return null;
  // The doors (sign-in, registration) with no connection: nothing on them says
  // so otherwise, and nothing of the account is on the device (web card 227).
  // The page stays mounted under it, so a half-typed form is kept.
  const doorsOffline = open && loaded && !me.authenticated && isDown(c);
  return (
    <>
      {doorsOffline && (
        <div className="signed-out-offline-wrap">
          <SignedOutOfflineNotice
            kind={c.online ? 'unreachable' : 'offline'}
            signedOut={signedOutHere()}
          />
        </div>
      )}
      {children}
    </>
  );
}

/**
 * A new page starts with nothing stored on it: the notice's "part of what you
 * see is a stored copy" belongs to the page that showed one.
 */
function FreshOnNavigation() {
  const { pathname } = useLocation();
  useEffect(() => {
    connectivity.freshAgain();
  }, [pathname]);
  // Asks the platform again while the connection is gone (`useConnectionWatch`).
  useConnectionWatch();
  return null;
}

/**
 * On the front page's host every path is the front page; the app itself lives on the platform
 * host. A member who is already signed in has no business on the front page: the session cookie
 * belongs to the parent domain, so send them to the platform host. Never while `/api/meta` or
 * `/api/auth/me` is still in flight, and never when `publicBaseUrl` is empty or points back at
 * this very host, which would be a redirect loop.
 */
function HostSwitch({ children }: { children: ReactNode }) {
  const { meta, loaded } = useMeta();
  const { me, loaded: authLoaded } = useAuth();
  const onRoot = loaded && !!meta.rootHost && window.location.hostname === meta.rootHost;
  const target = meta.publicBaseUrl || '';
  const elsewhere = hostOf(target) !== '' && hostOf(target) !== window.location.hostname;
  useEffect(() => {
    if (onRoot && authLoaded && me.authenticated && elsewhere) {
      leaving.to(target);
    }
  }, [onRoot, authLoaded, me.authenticated, elsewhere, target]);
  if (onRoot) return <Landing />;
  return children;
}

export function App() {
  return (
    <MetaProvider>
      <AuthProvider>
        {/* v7_startTransition and v7_relativeSplatPath are the only behaviour, not a flag, since v7. */}
        <BrowserRouter>
          <FreshOnNavigation />
          <HostSwitch>
            <AuthGate>
              <OnboardingGate>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/signin" element={<SignIn />} />
                  <Route path="/signout" element={<SignOut />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/welcome" element={<Welcome />} />
                  <Route path="/servers" element={<Servers />} />
                  <Route path="/servers/:id" element={<ServerDetail />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/parells-de-veu" element={<VoicePairs />} />
                  <Route path="/catalog" element={<Catalog />} />
                  <Route path="/catalog/terrain/:world" element={<TerrainDetail />} />
                  <Route path="/catalog/loadouts/new" element={<LoadoutEditor />} />
                  <Route path="/catalog/loadouts/:id" element={<LoadoutEditor />} />
                  <Route path="/new" element={<NewMissionServer />} />
                  <Route path="/new/map" element={<NewMissionMap />} />
                  <Route path="/new/enemy" element={<NewMissionEnemy />} />
                  <Route path="/new/intel" element={<NewMissionIntel />} />
                  <Route path="/new/type" element={<NewMissionType />} />
                  <Route path="/new/forces" element={<NewMissionForces />} />
                  <Route path="/new/start" element={<NewMissionStart />} />
                  <Route path="/new/decide" element={<NewMissionDecide />} />
                  <Route path="/sessions" element={<Sessions />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/library" element={<Library />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/novetats" element={<Changelog />} />
                  <Route path="/full-de-ruta" element={<Roadmap />} />
                  <Route path="/que-ve" element={<ComingUp />} />
                  <Route path="/estat" element={<Status />} />
                  <Route path="/proves" element={<Proves />} />
                  <Route path="/proves/:id" element={<Proves />} />
                  <Route path="/proves/inspeccions" element={<Inspections />} />
                  <Route path="/proves/inspeccions/resum" element={<InspectionSummary />} />
                  <Route
                    path="/proves/inspeccions/campanya/:id"
                    element={<InspectionCampaignStatus />}
                  />
                  <Route
                    path="/proves/inspeccions/campanya/:id/revisio"
                    element={<InspectionCampaignReview />}
                  />
                  <Route path="/proves/inspeccio/:code" element={<InspectionReview />} />
                  <Route path="/campaigns/new" element={<NewCampaign />} />
                  <Route path="/c/:code" element={<CampaignBoard />} />
                  <Route path="/m/:code" element={<MissionPage />} />
                  <Route path="/design/:code" element={<Designer />} />
                  <Route path="/s/:code" element={<SessionPage />} />
                  <Route path="/s/:code/briefing" element={<SessionPage briefing />} />
                  <Route path="/s/:code/tecnic" element={<SessionTechnical />} />
                  <Route path="/direct/:code" element={<DirectionPage />} />
                  <Route path="/u/:handle" element={<Profile />} />
                  <Route path="*" element={<Home />} />
                </Routes>
              </OnboardingGate>
            </AuthGate>
          </HostSwitch>
        </BrowserRouter>
      </AuthProvider>
    </MetaProvider>
  );
}
